import { CLEAR_SUM_TARGET, MAX_TILE_VALUE, MIN_TILE_VALUE } from "./helpers";
import { getNegativeDrawChance, getNegativeWeights } from "./progression";
import type { TileGenerationContext, TileGenerator, TileValue } from "./types";

const TILE_VALUES: TileValue[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const MAX_ADAPTIVE_BOOST = 0.28;
const GENERATOR_DEBUG_KEY = "tavela.generator.debug";

const BASE_WEIGHTS: Record<number, number> = {
  1: 14,
  2: 13,
  3: 11,
  4: 9,
  5: 8,
  6: 6,
  7: 4,
  8: 2.2,
  9: 1.1
};

const BOARD_REASON_MULTIPLIERS: Record<number, number> = {
  1: 1.2,
  2: 1.2,
  3: 1.15,
  4: 1.1,
  5: 1,
  6: 0.85,
  7: 0.7,
  8: 0.5,
  9: 0.35
};

const EARLY_PHASE_MULTIPLIERS: Record<number, number> = {
  1: 1.25,
  2: 1.22,
  3: 1.15,
  4: 1.08,
  5: 1,
  6: 0.85,
  7: 0.72,
  8: 0.5,
  9: 0.34
};

const MID_PHASE_MULTIPLIERS: Record<number, number> = {
  1: 1.12,
  2: 1.1,
  3: 1.06,
  4: 1.02,
  5: 1,
  6: 0.93,
  7: 0.84,
  8: 0.72,
  9: 0.55
};

const LATE_PHASE_MULTIPLIERS: Record<number, number> = {
  1: 1.02,
  2: 1.02,
  3: 1.01,
  4: 1,
  5: 1,
  6: 0.98,
  7: 0.95,
  8: 0.88,
  9: 0.8
};

type RoundPhase = "early" | "mid" | "late";
type RecentHighBuildNudge = {
  multipliers: Record<number, number>;
  active: boolean;
};

type WeightProfile = {
  weights: number[];
  phase: RoundPhase;
  adaptiveBiasApplied: boolean;
  earlyRoundGenerosityActive: boolean;
  singleTileRescueBiasActive: boolean;
  recentHighBuildNudgeActive: boolean;
  recentHighBuildHints: number[];
  boardSnapshot: Array<{ value: number; source: "dealt" | "built" }> | null;
};

function sumWeights(weights: number[]): number {
  return weights.reduce((sum, weight) => sum + weight, 0);
}

function sampleWeightedValue(weights: number[], randomSource: () => number): TileValue {
  const totalWeight = sumWeights(weights);
  let roll = randomSource() * totalWeight;

  for (let index = 0; index < weights.length; index += 1) {
    roll -= weights[index];
    if (roll <= 0) {
      return TILE_VALUES[index];
    }
  }

  return TILE_VALUES[TILE_VALUES.length - 1];
}

function sampleWeightedEntry(
  entries: ReadonlyArray<{ weight: number; value: TileValue }>,
  randomSource: () => number
): TileValue {
  const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0);
  let roll = randomSource() * totalWeight;

  for (const entry of entries) {
    roll -= entry.weight;
    if (roll <= 0) {
      return entry.value;
    }
  }

  return entries[entries.length - 1]?.value ?? TILE_VALUES[0];
}

function getRoundPhase(roundNumber: number): RoundPhase {
  if (roundNumber <= 3) {
    return "early";
  }

  if (roundNumber <= 7) {
    return "mid";
  }

  return "late";
}

function phaseMultiplier(value: TileValue, phase: RoundPhase): number {
  if (phase === "early") {
    return EARLY_PHASE_MULTIPLIERS[value];
  }

  if (phase === "mid") {
    return MID_PHASE_MULTIPLIERS[value];
  }

  return LATE_PHASE_MULTIPLIERS[value];
}

function adaptiveBoostForValue(
  value: TileValue,
  context: TileGenerationContext,
  boardValues: number[]
): number {
  if (context.reason === "board") {
    return 0;
  }

  if (boardValues.length === 0) {
    return 0;
  }

  const supportTiles = context.state === null ? [] : [...context.state.hand];
  const matchCount = boardValues.filter((tile) => tile === value).length;
  const legalMoveCount = boardValues.filter(
    (tile) => value === tile || value + tile <= CLEAR_SUM_TARGET
  ).length;
  const tenComplementCount = boardValues.filter(
    (tile) => value + tile === CLEAR_SUM_TARGET
  ).length;
  const supportCount = supportTiles.filter((tile) => tile === value).length;

  const rawBoost =
    matchCount * 0.07 +
    legalMoveCount * 0.035 +
    tenComplementCount * 0.025 +
    supportCount * 0.02;

  return Math.min(MAX_ADAPTIVE_BOOST, rawBoost);
}

function singleTileRescueMultiplier(
  value: TileValue,
  context: TileGenerationContext,
  boardValues: number[],
  phase: RoundPhase
): number {
  if (context.reason !== "hand" || boardValues.length !== 1) {
    return 1;
  }

  const loneBoardValue = boardValues[0];
  const complement = CLEAR_SUM_TARGET - loneBoardValue;
  const phaseScale = phase === "early" ? 1 : phase === "mid" ? 0.85 : 0.7;

  if (value === loneBoardValue) {
    return 1 + 0.5 * phaseScale;
  }

  if (value === complement && complement >= MIN_TILE_VALUE && complement <= MAX_TILE_VALUE) {
    return 1 + 0.32 * phaseScale;
  }

  return 1;
}

function recentHighBuildNudge(context: TileGenerationContext): RecentHighBuildNudge {
  if (context.reason !== "hand") {
    return {
      active: false,
      multipliers: {
        1: 1,
        2: 1,
        3: 1,
        4: 1,
        5: 1,
        6: 1,
        7: 1,
        8: 1,
        9: 1
      }
    };
  }

  const hints = context.state?.recentHighBuildHints ?? [];
  const multipliers: Record<number, number> = {
    1: 1,
    2: 1,
    3: 1,
    4: 1,
    5: 1,
    6: 1,
    7: 1,
    8: 1,
    9: 1
  };

  let active = false;
  for (const hint of hints) {
    const intensity = hint.ttl / 3;

    if (hint.value === 9) {
      multipliers[9] *= 1 + 0.07 * intensity;
      multipliers[1] *= 1 + 0.08 * intensity;
      active = true;
    } else if (hint.value === 8) {
      multipliers[8] *= 1 + 0.06 * intensity;
      multipliers[2] *= 1 + 0.07 * intensity;
      active = true;
    } else if (hint.value === 7) {
      multipliers[7] *= 1 + 0.05 * intensity;
      multipliers[3] *= 1 + 0.06 * intensity;
      active = true;
    }
  }

  return { multipliers, active };
}

function buildWeightProfile(context: TileGenerationContext): WeightProfile {
  const phase = getRoundPhase(context.roundNumber);
  const boardValues = context.state?.board.map((tile) => tile.value) ?? [];
  const recentHighBuild = recentHighBuildNudge(context);
  let adaptiveBiasApplied = false;
  let singleTileRescueBiasActive = false;
  const weights = TILE_VALUES.map((value) => {
    let weight = BASE_WEIGHTS[value] * phaseMultiplier(value, phase);

    const boardReasonMultiplier =
      context.reason === "board" ? BOARD_REASON_MULTIPLIERS[value] : 1;
    weight *= boardReasonMultiplier;

    const rescueMultiplier = singleTileRescueMultiplier(value, context, boardValues, phase);
    if (rescueMultiplier > 1.01) {
      singleTileRescueBiasActive = true;
    }
    weight *= rescueMultiplier;
    weight *= recentHighBuild.multipliers[value];

    const adaptiveBoost = adaptiveBoostForValue(value, context, boardValues);
    if (adaptiveBoost > 0.01) {
      adaptiveBiasApplied = true;
    }

    weight *= 1 + adaptiveBoost;
    return weight;
  });

  return {
    weights,
    phase,
    adaptiveBiasApplied,
    earlyRoundGenerosityActive: phase === "early",
    singleTileRescueBiasActive,
    recentHighBuildNudgeActive: recentHighBuild.active,
    recentHighBuildHints: (context.state?.recentHighBuildHints ?? []).map((hint) => hint.value),
    boardSnapshot: context.state?.board
      ? context.state.board.map((tile) => ({ value: tile.value, source: tile.source }))
      : null
  };
}

function topWeightedValues(weights: number[], count = 3): Array<{ value: number; weight: number }> {
  return weights
    .map((weight, index) => ({
      value: TILE_VALUES[index],
      weight: Number(weight.toFixed(3))
    }))
    .sort((left, right) => right.weight - left.weight)
    .slice(0, count);
}

function isGeneratorDebugEnabled(): boolean {
  const processLike = (globalThis as {
    process?: { env?: Record<string, string | undefined> };
  }).process;
  if (processLike?.env?.TAVELA_GENERATOR_DEBUG === "1") {
    return true;
  }

  const browserWindow = typeof window === "undefined" ? null : (window as typeof window & {
    localStorage?: Storage;
  });
  if (browserWindow?.localStorage === undefined) {
    return false;
  }

  try {
    return browserWindow.localStorage.getItem(GENERATOR_DEBUG_KEY) === "1";
  } catch {
    return false;
  }
}

function logGeneration(
  context: TileGenerationContext,
  value: TileValue,
  profile: WeightProfile
): void {
  if (!isGeneratorDebugEnabled()) {
    return;
  }

  console.info("[generator]", {
    reason: context.reason,
    roundNumber: context.roundNumber,
    generatedTile: value,
    phase: profile.phase,
    adaptiveBiasApplied: profile.adaptiveBiasApplied,
    earlyRoundGenerosityActive: profile.earlyRoundGenerosityActive,
    singleTileRescueBiasActive: profile.singleTileRescueBiasActive,
    recentHighBuildNudgeActive: profile.recentHighBuildNudgeActive,
    recentHighBuildHints: profile.recentHighBuildHints,
    boardSnapshot: profile.boardSnapshot,
    topCandidates: topWeightedValues(profile.weights)
  });
}

export function createRandomTileGenerator(
  randomSource: () => number = Math.random
): TileGenerator {
  return {
    nextRandom: randomSource,
    nextTile(context: TileGenerationContext): TileValue {
      if (context.reason === "hand") {
        const progression = {
          roundNumber: context.roundNumber,
          score: context.state?.score ?? 0
        };
        const negativeDrawChance = getNegativeDrawChance(progression);
        if (negativeDrawChance > 0 && randomSource() < negativeDrawChance) {
          return sampleWeightedEntry(getNegativeWeights(progression), randomSource);
        }
      }

      const profile = buildWeightProfile(context);
      const value = sampleWeightedValue(profile.weights, randomSource);
      logGeneration(context, value, profile);

      return Math.min(MAX_TILE_VALUE, Math.max(MIN_TILE_VALUE, value));
    }
  };
}
